/** Strings of the time-entry screens (my time, the team list, the new-entry form, the project list, time suggestions): `timeEntriesEn` is the source, `timeEntriesCs` must have the same keys. */
export const timeEntriesEn = {
  bulkBar: { selected: "{{label}} selected" },
  timeEntries: {
    common: {
      workItem: "Work item",
      overhead: "Overhead",
      overheadDefault: "{{name}} (default)",
      noProject: "No project",
      noProjects: "No projects",
      unassigned: "Unassigned",
      discarded: "Discarded",
      discard: "Discard",
      approve: "Approve",
      reject: "Reject",
      budget: "Budget",
      edit: "Edit",
      cancel: "Cancel",
      save: "Save",
      clear: "Clear",
      error: "Error",
      clockify: "Clockify: {{name}}",
      downloadCsv: "Download CSV",
      downloadPdf: "Download PDF",
    },
    approval: {
      worked: "worked",
      logged: "worked",
      budget: "budget",
      approved: "approved",
      paid: "paid out",
      rejected: "rejected",
    },
    billing: {
      unbilled: "unbilled",
      to_invoice: "to invoice",
      invoiced: "invoiced",
      paid: "invoice paid",
      non_billable: "non-billable",
      fixed_price: "fixed price",
    },
    mine: {
      title: "My time",
      subtitle:
        "Everything you've logged, whichever way it got here — the timer, by hand, or a confirmed <1>time suggestion</1>.",
      logTime: "Log time",
      date: "Date",
      state: "State",
      fromDate: "From date",
      toDate: "To date",
      summary_one: "{{hours}} over {{count}} entry",
      summary_few: "{{hours}} over {{count}} entries",
      summary_other: "{{hours}} over {{count}} entries",
      empty: "Nothing logged in this range.",
      dateRange: "Date range",
      tabs: { summary: "Summary", detailed: "Detailed" },
      kpi: {
        total: "Total",
        entries: "Entries",
        days: "Days worked",
        perDay: "Average per worked day",
      },
      lastWeek: "Last 7 days",
      lastMonth: "Last 30 days",
      byProject: "By project",
      share: "Share",
      noProjectRow: "No project",
    },
    newEntry: {
      title: "New Time Entry",
      subtitle: "Log time against a work item.",
      details: "Entry Details",
      detailsHint: "Select a project and work item, then enter the time spent.",
      selectProject: "Select a project...",
      selectProjectFirst: "Select a project first",
      pickTarget: "Pick a work item or overhead…",
      noTargets: "No work items or overheads — add some in project settings",
      date: "Date",
      start: "Start",
      optional: "(optional)",
      descriptionPlaceholder: "What did you work on?",
      create: "Create Time Entry",
      creating: "Creating…",
      createFailed: "Couldn't create the time entry.",
      required: "Project, a work item or overhead, date and a positive hours value are required.",
    },
    all: {
      title: "Time Entries",
      subtitle:
        "Every time entry in one list. Rows still awaiting a work item are pinned to the top — pick one and they join the list as a linked entry.",
      newEntry: "New Entry",
      search: "Search by work item, description or user...",
      entries_one: "{{count}} entry",
      entries_few: "{{count}} entries",
      entries_other: "{{count}} entries",
      awaiting_one: "{{count}} awaiting assignment on this page",
      awaiting_few: "{{count}} awaiting assignment on this page",
      awaiting_other: "{{count}} awaiting assignment on this page",
      discardedCount_one: "{{count}} discarded",
      discardedCount_few: "{{count}} discarded",
      discardedCount_other: "{{count}} discarded",
      showDiscarded: "Show discarded",
      hideDiscarded: "Hide discarded",
      allMatchingSelected_one: "The one matching entry is selected.",
      allMatchingSelected_few: "All {{count}} matching entries are selected.",
      allMatchingSelected_other: "All {{count}} matching entries are selected.",
      restrictToPage: "Restrict to this page",
      pageSelected_one: "The one entry on this page is selected.",
      pageSelected_few: "All {{count}} entries on this page are selected.",
      pageSelected_other: "All {{count}} entries on this page are selected.",
      selectAllMatching_one: "Select the one matching entry",
      selectAllMatching_few: "Select all {{count}} matching entries",
      selectAllMatching_other: "Select all {{count}} matching entries",
      fetchFailed: "Couldn't fetch the full result",
      empty: "No time entries yet.",
      selectEntry: "Select entry {{id}}",
      selectRow: "Select row",
      selectAllEntries: "Select all entries",
    },
    projectPage: {
      summary_one: "{{count}} entry · {{hours}} total",
      summary_few: "{{count}} entries · {{hours}} total",
      summary_other: "{{count}} entries · {{hours}} total",
      search: "Search by work item, user or description...",
      projectOverhead: "Project overhead",
      source: "Source",
      approval: "Approval",
    },
    suggestions: {
      title: "Time suggestions",
      subtitle:
        "Proposed time entries from your time tracker and from meetings you attended. Nothing is logged until you confirm it, and today's is confirmed tomorrow — while you're working, its length can still grow. Connect a tracker, and check that meetings can recognise you, on your <1>profile</1>.",
    },
    suggestionsAll: {
      title: "Team time suggestions",
      subtitle:
        "Proposed time entries for everyone — from automatic time trackers and from analysed meetings. Nothing is logged until it's confirmed, and today's is confirmed tomorrow — a tracker keeps revising the block someone is still working in. Proposals whose owner couldn't be matched to a Hub account show up here and nowhere else. Your own are on <1>Time suggestions</1>.",
    },
    columns: {
      start: "Start",
      user: "User",
      project: "Project",
      workItem: "Work Item",
      time: "Time",
      hours: "Hours",
      status: "Status",
      billing: "Billing",
      actions: "Actions",
      description: "Description",
      externalUser: "External user",
      clockifyUserNotMirrored: "Clockify user not mirrored in Hub",
      clockifyProjectNotMirrored: "Clockify project not mirrored in Hub",
      clockifyProject: "Clockify project",
      projectOverhead: "Project overhead: {{name}}",
      overheadName: "Overhead: {{name}}",
      unassignedHint: "No work item or overhead picked yet — pick one or mark overhead.",
      hint: "Hint: #{{ref}}",
      onReport: "On report",
      onReportHint: "This entry is on a report — its billing follows the report's status",
      approveTomorrow: "Approve tomorrow",
      approveTomorrowHint:
        "Time is approved a day later — today's is still being worked, so its length can still grow.",
      budgetHint: "Park: pay this later. The TE stays un-approved until the decision is made.",
      revokeHint: "Revoke approval — typically a manager correcting an accidental Approve.",
      markPaidOut: "Mark paid out",
      unmarkPaidOut: "Unmark paid out",
      discardHint: "Discard: drop this row. It can be restored later.",
    },
    rows: {
      workItemOrOverhead: "Work item or overhead",
      pickProjectFirst: "— pick project first —",
      pickProject: "— pick project —",
      pickWorkItem: "— pick work item —",
      pickWorkItemOrOverhead: "— pick work item or overhead —",
      noOpenIssues: "— no open issues —",
      hubProject: "Hub project",
      projectOverheadOption: "— Project overhead (no work item) —",
      workItemUpdated: "Work item updated",
      overheadUpdated: "Overhead updated",
      changeTargetFailed: "Couldn't change target",
      entryDiscarded: "Entry discarded",
      workItemAssigned: "Work item assigned",
      markedOverhead: "Marked as overhead",
      projectBound: "Project bound",
      actionFailed: "Action failed",
      selectUnassigned: "Select unassigned entry {{id}}",
      clockifyReferenced: "Clockify referenced this work item",
      looksLike: "looks like #{{ref}}",
      clockifyHinted: "Clockify hinted at #{{ref}}",
      billable: "billable",
      discarding: "Discarding",
      markBillable: "Mark billable",
      markNonBillable: "Mark non-billable",
      markedBillable: "Marked billable",
      markedNonBillable: "Marked non-billable",
      updateFailed: "Update failed",
      saving: "Saving",
      entryRestored: "Entry restored",
      restoreFailed: "Restore failed",
      restore: "Restore",
      restoring: "Restoring",
    },
    filters: {
      unnamed: "(unnamed)",
      hubUsers: "Hub users",
      hubProjects: "Hub projects",
      clockifyOnly: "Clockify-only",
      special: "Special",
      workItems: "Work items",
      who: "Who",
      what: "What",
      state: "State",
      when: "When",
      allUsers: "All users",
      usersNoun: "users",
      byUser: "Filter by user",
      allProjects: "All projects",
      projectsNoun: "projects",
      byProject: "Filter by project",
      allWorkItems: "All work items",
      workItemsNoun: "work items",
      byWorkItem: "Filter by work item",
      allStatuses: "All statuses",
      statusesNoun: "statuses",
      byStatus: "Filter by status",
      allBilling: "All billing",
      billingNoun: "billing states",
      byBilling: "Filter by billing",
      allTags: "All tags",
      tagsNoun: "tags",
      byTag: "Filter by tag",
      fromDateTime: "From date and time",
      toDateTime: "To date and time",
      from: "From",
      to: "To",
      byDateRange: "Filter by date range",
      allTime: "All time",
      thisMonth: "This month",
      lastMonth: "Last month",
      customRange: "Custom range",
    },
    bulk: {
      done: {
        approve_one: "{{count}} entry approved",
        approve_few: "{{count}} entries approved",
        approve_other: "{{count}} entries approved",
        budget_one: "{{count}} entry parked in budget",
        budget_few: "{{count}} entries parked in budget",
        budget_other: "{{count}} entries parked in budget",
        reject_one: "{{count}} entry rejected",
        reject_few: "{{count}} entries rejected",
        reject_other: "{{count}} entries rejected",
        mark_paid_one: "{{count}} entry marked paid",
        mark_paid_few: "{{count}} entries marked paid",
        mark_paid_other: "{{count}} entries marked paid",
        unmark_paid_one: "{{count}} entry unmarked paid",
        unmark_paid_few: "{{count}} entries unmarked paid",
        unmark_paid_other: "{{count}} entries unmarked paid",
        mark_overhead_one: "{{count}} entry marked as overhead",
        mark_overhead_few: "{{count}} entries marked as overhead",
        mark_overhead_other: "{{count}} entries marked as overhead",
        discard_one: "{{count}} entry discarded",
        discard_few: "{{count}} entries discarded",
        discard_other: "{{count}} entries discarded",
        bind_project_one: "{{count}} entry bound to a project",
        bind_project_few: "{{count}} entries bound to a project",
        bind_project_other: "{{count}} entries bound to a project",
        bind_work_item_one: "{{count}} entry bound to a work item",
        bind_work_item_few: "{{count}} entries bound to a work item",
        bind_work_item_other: "{{count}} entries bound to a work item",
        set_billing_one: "Billing set on {{count}} entry",
        set_billing_few: "Billing set on {{count}} entries",
        set_billing_other: "Billing set on {{count}} entries",
        set_approval_one: "{{count}} entry updated",
        set_approval_few: "{{count}} entries updated",
        set_approval_other: "{{count}} entries updated",
        add_tags_one: "{{count}} entry tagged",
        add_tags_few: "{{count}} entries tagged",
        add_tags_other: "{{count}} entries tagged",
        remove_tags_one: "{{count}} entry untagged",
        remove_tags_few: "{{count}} entries untagged",
        remove_tags_other: "{{count}} entries untagged",
        reassign_user_one: "{{count}} entry reassigned",
        reassign_user_few: "{{count}} entries reassigned",
        reassign_user_other: "{{count}} entries reassigned",
      },
      nothingChanged: "Nothing changed",
      noop: {
        default: "No selected row matched this action.",
        mark_paid: "Only approved rows can be marked paid out — approve them first.",
        unmark_paid: "Only paid-out rows can be reverted.",
        approve:
          "These rows aren't in a state that can be approved — or their day hasn't closed yet, and time is approved a day later.",
        budget: "Only worked rows can be parked in budget.",
        reject: "These rows aren't in a state that can be rejected.",
        set_billing:
          "Report-linked rows are locked — their billing follows the report. Remove them from the report first.",
        discardAll: "Invoice-linked rows can't be discarded — remove them from the invoice first.",
        discardOwn: "Only your own worked, non-invoiced rows can be discarded.",
        mark_overhead: "Only your own worked, project-bound rows can be set to overhead.",
      },
      failed: "Bulk action failed",
      reportCreated: "Report created",
      reportFailed: "Couldn't create report",
    },
    bars: {
      entry: "entry",
      entries: "entries",
      unassignedEntry: "unassigned entry",
      unassignedEntries: "unassigned entries",
      csv: "CSV",
      pdf: "PDF",
      approvesHint_one: "Approves {{count}} — {{open}} from today can be approved tomorrow.",
      approvesHint_few: "Approves {{count}} — {{open}} from today can be approved tomorrow.",
      approvesHint_other: "Approves {{count}} — {{open}} from today can be approved tomorrow.",
      approveCount: "Approve ({{count}})",
      todayApprovedTomorrow: "Today's time is approved tomorrow",
      budgetHint:
        "Park: pay this later. The TE stays un-approved until a manager flips it to approved or rejected.",
      bulkBindProject: "Bulk bind project",
      bindToProject: "— bind to project —",
      bindProject: "Bind project",
      bindToWorkItem: "— bind to work item —",
      noOpenWorkItems: "No open work items in this project",
      bulkBindWorkItem: "Bulk bind work item",
      bindWorkItem: "Bind work item",
      markOverhead: "Mark overhead",
      markOverheadHint: "Strip the work item — file these as project overhead instead.",
      bulkApprovalTarget: "Bulk approval target",
      approvalNoChange: "— approval (no change) —",
      bulkBillingTarget: "Bulk billing target",
      billingNoChange: "— billing (no change) —",
      apply: "Apply",
      sendToReport: "Send to existing report",
      createReport: "Create new report",
      pairToInvoice: "Pair to issued invoice",
      editSelected: "Edit selected…",
    },
    adminEdit: {
      title: "Edit time entry",
      adminOnly: "Admin-only.",
      loading: "Loading…",
      loadingHistory: "Loading history…",
      noChanges: "No recorded changes yet.",
      action: {
        create: "create",
        update: "update",
        delete: "delete",
        restore: "restore",
        split: "split",
      },
      field: {
        description: "Description",
        hours: "Hours",
        startedAt: "Start",
        endedAt: "End",
        spentOn: "Day",
        projectId: "Project",
        workItemId: "Work item",
        overheadId: "Overhead",
        userId: "User",
        approvalState: "Status",
        billingState: "Billing",
        billable: "Billable",
        tags: "Tags",
      },
      unknownActor: "unknown",
      apiToken: "API token {{name}}",
      entryDeleted: "Entry deleted — the full row is kept for restoring.",
      splitInto_one: "Split into {{count}} piece.",
      splitInto_few: "Split into {{count}} pieces.",
      splitInto_other: "Split into {{count}} pieces.",
      showClosed: "Show closed",
      pickProjectFirst: "Pick a project first",
      noTargets: "No work items or overheads",
      startPrague: "Start (Prague wall time)",
      endPrague: "End (Prague wall time)",
      orphan: "(orphan — currently {{email}})",
      external: "External",
      billingLocked: "(locked — fixed-price WI)",
      tags: "Tags",
      noTags: "No tags.",
      removeTag: "Remove tag {{tag}}",
      addTagPlaceholder: "Add a tag…",
      add: "Add",
      history: "History",
      saving: "Saving…",
      updated: "Time entry updated",
      updateFailed: "Update failed",
    },
    adminBulk: {
      title_one: "Edit {{count}} time entry",
      title_few: "Edit {{count}} time entries",
      title_other: "Edit {{count}} time entries",
      description:
        "Tick the fields you want to rewrite across every selected entry. Untouched fields keep their per-row value.",
      targetHintPicked: "From the picked project's list.",
      targetHintCurrent: "From the rows' current projects — projects must be uniform first.",
      pick: "— pick —",
      showClosedWorkItems: "Show closed work items",
      pickUser: "— pick user —",
      billingHint: "Rows on a fixed-price WI keep their lock — server returns 409 on them.",
      addTags: "Add tags",
      addTagsPlaceholder: "comma-separated, e.g. backend, urgent",
      removeTags: "Remove tags",
      removeTagsPlaceholder: "comma-separated",
      save_one: "Save changes to {{count}} entry",
      save_few: "Save changes to {{count}} entries",
      save_other: "Save changes to {{count}} entries",
      pickField: "Pick a field to update first",
      confirmReassign_one: "Reassign {{count}} entry to the picked user?",
      confirmReassign_few: "Reassign {{count}} entries to the picked user?",
      confirmReassign_other: "Reassign {{count}} entries to the picked user?",
      confirmAddTags_one: "Add tags [{{tags}}] to {{count}} entry?",
      confirmAddTags_few: "Add tags [{{tags}}] to {{count}} entries?",
      confirmAddTags_other: "Add tags [{{tags}}] to {{count}} entries?",
      updated_one: "Updated {{count}} entry",
      updated_few: "Updated {{count}} entries",
      updated_other: "Updated {{count}} entries",
      failed: "Bulk update failed",
    },
  },
};

export const timeEntriesCs: typeof timeEntriesEn = {
  bulkBar: { selected: "{{label}} vybráno" },
  timeEntries: {
    common: {
      workItem: "Tiket",
      overhead: "Režie",
      overheadDefault: "{{name}} (výchozí)",
      noProject: "Bez projektu",
      noProjects: "Žádné projekty",
      unassigned: "Nepřiřazeno",
      discarded: "Vyřazeno",
      discard: "Vyřadit",
      approve: "Schválit",
      reject: "Zamítnout",
      budget: "Rozpočet",
      edit: "Upravit",
      cancel: "Zrušit",
      save: "Uložit",
      clear: "Vymazat",
      error: "Chyba",
      clockify: "Clockify: {{name}}",
      downloadCsv: "Stáhnout CSV",
      downloadPdf: "Stáhnout PDF",
    },
    approval: {
      worked: "odpracováno",
      logged: "odpracováno",
      budget: "rozpočet",
      approved: "schváleno",
      paid: "vyplaceno",
      rejected: "zamítnuto",
    },
    billing: {
      unbilled: "nefakturováno",
      to_invoice: "k fakturaci",
      invoiced: "fakturováno",
      paid: "faktura zaplacena",
      non_billable: "nefakturovatelné",
      fixed_price: "paušál",
    },
    mine: {
      title: "Můj čas",
      subtitle:
        "Všechno, co jste vykázali, ať už se to sem dostalo jakkoli — časovačem, ručně nebo potvrzeným <1>návrhem času</1>.",
      logTime: "Vykázat čas",
      date: "Datum",
      state: "Stav",
      fromDate: "Od data",
      toDate: "Do data",
      summary_one: "{{hours}} za {{count}} záznam",
      summary_few: "{{hours}} za {{count}} záznamy",
      summary_other: "{{hours}} za {{count}} záznamů",
      empty: "V tomto období nic nevykázáno.",
      dateRange: "Období",
      tabs: { summary: "Souhrn", detailed: "Detail" },
      kpi: {
        total: "Celkem",
        entries: "Záznamů",
        days: "Odpracovaných dnů",
        perDay: "Průměr na odpracovaný den",
      },
      lastWeek: "Posledních 7 dní",
      lastMonth: "Posledních 30 dní",
      byProject: "Podle projektu",
      share: "Podíl",
      noProjectRow: "Bez projektu",
    },
    newEntry: {
      title: "Nový záznam času",
      subtitle: "Vykažte čas na tiket.",
      details: "Údaje o záznamu",
      detailsHint: "Vyberte projekt a tiket, pak zadejte strávený čas.",
      selectProject: "Vyberte projekt...",
      selectProjectFirst: "Nejdřív vyberte projekt",
      pickTarget: "Vyberte tiket nebo režii…",
      noTargets: "Žádné tikety ani režie — přidejte je v nastavení projektu",
      date: "Datum",
      start: "Začátek",
      optional: "(volitelné)",
      descriptionPlaceholder: "Na čem jste pracovali?",
      create: "Vytvořit záznam času",
      creating: "Vytvářím…",
      createFailed: "Záznam času se nepodařilo vytvořit.",
      required: "Je potřeba projekt, tiket nebo režie, datum a kladný počet hodin.",
    },
    all: {
      title: "Záznamy času",
      subtitle:
        "Všechny záznamy času v jednom seznamu. Řádky, které ještě čekají na tiket, jsou připnuté nahoře — vyberte tiket a řádek se přidá do seznamu jako přiřazený záznam.",
      newEntry: "Nový záznam",
      search: "Hledat podle tiketu, popisu nebo uživatele...",
      entries_one: "{{count}} záznam",
      entries_few: "{{count}} záznamy",
      entries_other: "{{count}} záznamů",
      awaiting_one: "{{count}} čeká na přiřazení na této stránce",
      awaiting_few: "{{count}} čekají na přiřazení na této stránce",
      awaiting_other: "{{count}} čeká na přiřazení na této stránce",
      discardedCount_one: "{{count}} vyřazen",
      discardedCount_few: "{{count}} vyřazeny",
      discardedCount_other: "{{count}} vyřazeno",
      showDiscarded: "Zobrazit vyřazené",
      hideDiscarded: "Skrýt vyřazené",
      allMatchingSelected_one: "Je vybrán jediný odpovídající záznam.",
      allMatchingSelected_few: "Všechny {{count}} odpovídající záznamy jsou vybrány.",
      allMatchingSelected_other: "Všech {{count}} odpovídajících záznamů je vybráno.",
      restrictToPage: "Omezit na tuto stránku",
      pageSelected_one: "Je vybrán jediný záznam na této stránce.",
      pageSelected_few: "Všechny {{count}} záznamy na této stránce jsou vybrány.",
      pageSelected_other: "Všech {{count}} záznamů na této stránce je vybráno.",
      selectAllMatching_one: "Vybrat jediný odpovídající záznam",
      selectAllMatching_few: "Vybrat všechny {{count}} odpovídající záznamy",
      selectAllMatching_other: "Vybrat všech {{count}} odpovídajících záznamů",
      fetchFailed: "Celý výsledek se nepodařilo načíst",
      empty: "Zatím žádné záznamy času.",
      selectEntry: "Vybrat záznam {{id}}",
      selectRow: "Vybrat řádek",
      selectAllEntries: "Vybrat všechny záznamy",
    },
    projectPage: {
      summary_one: "{{count}} záznam · celkem {{hours}}",
      summary_few: "{{count}} záznamy · celkem {{hours}}",
      summary_other: "{{count}} záznamů · celkem {{hours}}",
      search: "Hledat podle tiketu, uživatele nebo popisu...",
      projectOverhead: "Režie projektu",
      source: "Zdroj",
      approval: "Schválení",
    },
    suggestions: {
      title: "Návrhy času",
      subtitle:
        "Navržené záznamy času z vašeho trackeru a ze schůzek, kterých jste se zúčastnili. Nic se nevykáže, dokud to nepotvrdíte, a dnešní čas se potvrzuje až zítra — dokud pracujete, jeho délka ještě může narůst. Tracker si připojíte a zda vás schůzky rozpoznají, zkontrolujete ve svém <1>profilu</1>.",
    },
    suggestionsAll: {
      title: "Návrhy času týmu",
      subtitle:
        "Navržené záznamy času pro všechny — z automatických trackerů času a z analyzovaných schůzek. Nic se nevykáže, dokud to není potvrzeno, a dnešní čas se potvrzuje až zítra — tracker stále upravuje blok, ve kterém někdo ještě pracuje. Návrhy, jejichž vlastníka se nepodařilo přiřadit k účtu v Hubu, se zobrazí jen tady. Vaše vlastní najdete v <1>Návrzích času</1>.",
    },
    columns: {
      start: "Začátek",
      user: "Uživatel",
      project: "Projekt",
      workItem: "Tiket",
      time: "Čas",
      hours: "Hodiny",
      status: "Stav",
      billing: "Fakturace",
      actions: "Akce",
      description: "Popis",
      externalUser: "Externí uživatel",
      clockifyUserNotMirrored: "Uživatel z Clockify, který v Hubu nemá účet",
      clockifyProjectNotMirrored: "Projekt z Clockify, který v Hubu neexistuje",
      clockifyProject: "Projekt v Clockify",
      projectOverhead: "Režie projektu: {{name}}",
      overheadName: "Režie: {{name}}",
      unassignedHint:
        "Zatím není vybrán tiket ani režie — vyberte jedno z nich nebo označte režii.",
      hint: "Nápověda: #{{ref}}",
      onReport: "Ve výkazu",
      onReportHint: "Tento záznam je ve výkazu — jeho fakturace se řídí stavem výkazu",
      approveTomorrow: "Schválit zítra",
      approveTomorrowHint:
        "Čas se schvaluje až o den později — dnešní se ještě odpracovává, takže jeho délka může narůst.",
      budgetHint: "Odložit: zaplatit později. Záznam zůstane neschválený, dokud se nerozhodne.",
      revokeHint: "Odvolat schválení — obvykle když manažer opravuje omylem schválený záznam.",
      markPaidOut: "Označit jako vyplacené",
      unmarkPaidOut: "Zrušit označení vyplaceno",
      discardHint: "Vyřadit: odstranit tento řádek. Později ho lze obnovit.",
    },
    rows: {
      workItemOrOverhead: "Tiket nebo režie",
      pickProjectFirst: "— nejdřív vyberte projekt —",
      pickProject: "— vyberte projekt —",
      pickWorkItem: "— vyberte tiket —",
      pickWorkItemOrOverhead: "— vyberte tiket nebo režii —",
      noOpenIssues: "— žádné otevřené tikety —",
      hubProject: "Projekt v Hubu",
      projectOverheadOption: "— Režie projektu (bez tiketu) —",
      workItemUpdated: "Tiket změněn",
      overheadUpdated: "Režie změněna",
      changeTargetFailed: "Cíl se nepodařilo změnit",
      entryDiscarded: "Záznam vyřazen",
      workItemAssigned: "Tiket přiřazen",
      markedOverhead: "Označeno jako režie",
      projectBound: "Projekt přiřazen",
      actionFailed: "Akce se nezdařila",
      selectUnassigned: "Vybrat nepřiřazený záznam {{id}}",
      clockifyReferenced: "Clockify odkazovalo na tento tiket",
      looksLike: "vypadá jako #{{ref}}",
      clockifyHinted: "Clockify naznačilo #{{ref}}",
      billable: "fakturovatelné",
      discarding: "Vyřazuji",
      markBillable: "Označit jako fakturovatelné",
      markNonBillable: "Označit jako nefakturovatelné",
      markedBillable: "Označeno jako fakturovatelné",
      markedNonBillable: "Označeno jako nefakturovatelné",
      updateFailed: "Úprava se nezdařila",
      saving: "Ukládám",
      entryRestored: "Záznam obnoven",
      restoreFailed: "Obnovení se nezdařilo",
      restore: "Obnovit",
      restoring: "Obnovuji",
    },
    filters: {
      unnamed: "(bez jména)",
      hubUsers: "Uživatelé Hubu",
      hubProjects: "Projekty Hubu",
      clockifyOnly: "Jen v Clockify",
      special: "Speciální",
      workItems: "Tikety",
      who: "Kdo",
      what: "Co",
      state: "Stav",
      when: "Kdy",
      allUsers: "Všichni uživatelé",
      usersNoun: "uživatelů",
      byUser: "Filtrovat podle uživatele",
      allProjects: "Všechny projekty",
      projectsNoun: "projektů",
      byProject: "Filtrovat podle projektu",
      allWorkItems: "Všechny tikety",
      workItemsNoun: "tiketů",
      byWorkItem: "Filtrovat podle tiketu",
      allStatuses: "Všechny stavy",
      statusesNoun: "stavů",
      byStatus: "Filtrovat podle stavu",
      allBilling: "Veškerá fakturace",
      billingNoun: "stavů fakturace",
      byBilling: "Filtrovat podle fakturace",
      allTags: "Všechny štítky",
      tagsNoun: "štítků",
      byTag: "Filtrovat podle štítku",
      fromDateTime: "Od data a času",
      toDateTime: "Do data a času",
      from: "Od",
      to: "Do",
      byDateRange: "Filtrovat podle období",
      allTime: "Celé období",
      thisMonth: "Tento měsíc",
      lastMonth: "Minulý měsíc",
      customRange: "Vlastní rozsah",
    },
    bulk: {
      done: {
        approve_one: "{{count}} záznam schválen",
        approve_few: "{{count}} záznamy schváleny",
        approve_other: "{{count}} záznamů schváleno",
        budget_one: "{{count}} záznam odložen do rozpočtu",
        budget_few: "{{count}} záznamy odloženy do rozpočtu",
        budget_other: "{{count}} záznamů odloženo do rozpočtu",
        reject_one: "{{count}} záznam zamítnut",
        reject_few: "{{count}} záznamy zamítnuty",
        reject_other: "{{count}} záznamů zamítnuto",
        mark_paid_one: "{{count}} záznam označen jako vyplacený",
        mark_paid_few: "{{count}} záznamy označeny jako vyplacené",
        mark_paid_other: "{{count}} záznamů označeno jako vyplacené",
        unmark_paid_one: "U {{count}} záznamu zrušeno označení vyplaceno",
        unmark_paid_few: "U {{count}} záznamů zrušeno označení vyplaceno",
        unmark_paid_other: "U {{count}} záznamů zrušeno označení vyplaceno",
        mark_overhead_one: "{{count}} záznam označen jako režie",
        mark_overhead_few: "{{count}} záznamy označeny jako režie",
        mark_overhead_other: "{{count}} záznamů označeno jako režie",
        discard_one: "{{count}} záznam vyřazen",
        discard_few: "{{count}} záznamy vyřazeny",
        discard_other: "{{count}} záznamů vyřazeno",
        bind_project_one: "{{count}} záznam přiřazen k projektu",
        bind_project_few: "{{count}} záznamy přiřazeny k projektu",
        bind_project_other: "{{count}} záznamů přiřazeno k projektu",
        bind_work_item_one: "{{count}} záznam přiřazen k tiketu",
        bind_work_item_few: "{{count}} záznamy přiřazeny k tiketu",
        bind_work_item_other: "{{count}} záznamů přiřazeno k tiketu",
        set_billing_one: "Fakturace nastavena u {{count}} záznamu",
        set_billing_few: "Fakturace nastavena u {{count}} záznamů",
        set_billing_other: "Fakturace nastavena u {{count}} záznamů",
        set_approval_one: "{{count}} záznam upraven",
        set_approval_few: "{{count}} záznamy upraveny",
        set_approval_other: "{{count}} záznamů upraveno",
        add_tags_one: "{{count}} záznam označen štítkem",
        add_tags_few: "{{count}} záznamy označeny štítkem",
        add_tags_other: "{{count}} záznamů označeno štítkem",
        remove_tags_one: "U {{count}} záznamu odebrán štítek",
        remove_tags_few: "U {{count}} záznamů odebrán štítek",
        remove_tags_other: "U {{count}} záznamů odebrán štítek",
        reassign_user_one: "{{count}} záznam přiřazen jinému uživateli",
        reassign_user_few: "{{count}} záznamy přiřazeny jinému uživateli",
        reassign_user_other: "{{count}} záznamů přiřazeno jinému uživateli",
      },
      nothingChanged: "Nic se nezměnilo",
      noop: {
        default: "Žádný vybraný řádek této akci neodpovídal.",
        mark_paid: "Jako vyplacené lze označit jen schválené řádky — nejdřív je schvalte.",
        unmark_paid: "Vrátit lze jen řádky označené jako vyplacené.",
        approve:
          "Tyto řádky nejsou ve stavu, který lze schválit — nebo jejich den ještě neskončil a čas se schvaluje o den později.",
        budget: "Do rozpočtu lze odložit jen odpracované řádky.",
        reject: "Tyto řádky nejsou ve stavu, který lze zamítnout.",
        set_billing:
          "Řádky ve výkazu jsou zamčené — jejich fakturace se řídí výkazem. Nejdřív je z výkazu odeberte.",
        discardAll: "Řádky navázané na fakturu nelze vyřadit — nejdřív je z faktury odeberte.",
        discardOwn: "Vyřadit lze jen vaše vlastní odpracované řádky bez faktury.",
        mark_overhead: "Jako režii lze označit jen vaše vlastní odpracované řádky s projektem.",
      },
      failed: "Hromadná akce se nezdařila",
      reportCreated: "Výkaz vytvořen",
      reportFailed: "Výkaz se nepodařilo vytvořit",
    },
    bars: {
      entry: "záznam",
      entries: "záznamů",
      unassignedEntry: "nepřiřazený záznam",
      unassignedEntries: "nepřiřazených záznamů",
      csv: "CSV",
      pdf: "PDF",
      approvesHint_one: "Schválí {{count}} — {{open}} z dneška lze schválit zítra.",
      approvesHint_few: "Schválí {{count}} — {{open}} z dneška lze schválit zítra.",
      approvesHint_other: "Schválí {{count}} — {{open}} z dneška lze schválit zítra.",
      approveCount: "Schválit ({{count}})",
      todayApprovedTomorrow: "Dnešní čas se schvaluje zítra",
      budgetHint:
        "Odložit: zaplatit později. Záznam zůstane neschválený, dokud ho manažer neschválí nebo nezamítne.",
      bulkBindProject: "Hromadně přiřadit projekt",
      bindToProject: "— přiřadit k projektu —",
      bindProject: "Přiřadit projekt",
      bindToWorkItem: "— přiřadit k tiketu —",
      noOpenWorkItems: "V tomto projektu nejsou žádné otevřené tikety",
      bulkBindWorkItem: "Hromadně přiřadit tiket",
      bindWorkItem: "Přiřadit tiket",
      markOverhead: "Označit jako režii",
      markOverheadHint: "Odebrat tiket — zařadit tyto záznamy místo toho do režie projektu.",
      bulkApprovalTarget: "Cílový stav schválení pro výběr",
      approvalNoChange: "— schválení (beze změny) —",
      bulkBillingTarget: "Cílový stav fakturace pro výběr",
      billingNoChange: "— fakturace (beze změny) —",
      apply: "Použít",
      sendToReport: "Poslat do existujícího výkazu",
      createReport: "Vytvořit nový výkaz",
      pairToInvoice: "Spárovat s vystavenou fakturou",
      editSelected: "Upravit vybrané…",
    },
    adminEdit: {
      title: "Upravit záznam času",
      adminOnly: "Jen pro administrátory.",
      loading: "Načítám…",
      loadingHistory: "Načítám historii…",
      noChanges: "Zatím žádné zaznamenané změny.",
      action: {
        create: "vytvoření",
        update: "úprava",
        delete: "smazání",
        restore: "obnovení",
        split: "rozdělení",
      },
      field: {
        description: "Popis",
        hours: "Hodiny",
        startedAt: "Začátek",
        endedAt: "Konec",
        spentOn: "Den",
        projectId: "Projekt",
        workItemId: "Tiket",
        overheadId: "Režie",
        userId: "Uživatel",
        approvalState: "Stav",
        billingState: "Fakturace",
        billable: "Fakturovatelné",
        tags: "Štítky",
      },
      unknownActor: "neznámý",
      apiToken: "API token {{name}}",
      entryDeleted: "Záznam smazán — celý řádek zůstává uložený pro obnovení.",
      splitInto_one: "Rozdělen na {{count}} část.",
      splitInto_few: "Rozdělen na {{count}} části.",
      splitInto_other: "Rozdělen na {{count}} částí.",
      showClosed: "Zobrazit uzavřené",
      pickProjectFirst: "Nejdřív vyberte projekt",
      noTargets: "Žádné tikety ani režie",
      startPrague: "Začátek (pražský čas)",
      endPrague: "Konec (pražský čas)",
      orphan: "(bez účtu — nyní {{email}})",
      external: "Externí",
      billingLocked: "(zamčeno — tiket s pevnou cenou)",
      tags: "Štítky",
      noTags: "Žádné štítky.",
      removeTag: "Odebrat štítek {{tag}}",
      addTagPlaceholder: "Přidat štítek…",
      add: "Přidat",
      history: "Historie",
      saving: "Ukládám…",
      updated: "Záznam času upraven",
      updateFailed: "Úprava se nezdařila",
    },
    adminBulk: {
      title_one: "Upravit {{count}} záznam času",
      title_few: "Upravit {{count}} záznamy času",
      title_other: "Upravit {{count}} záznamů času",
      description:
        "Zaškrtněte pole, která chcete přepsat u všech vybraných záznamů. Nedotčená pole si ponechají hodnotu z jednotlivých řádků.",
      targetHintPicked: "Ze seznamu vybraného projektu.",
      targetHintCurrent: "Z aktuálních projektů řádků — nejdřív musí mít všechny stejný projekt.",
      pick: "— vyberte —",
      showClosedWorkItems: "Zobrazit uzavřené tikety",
      pickUser: "— vyberte uživatele —",
      billingHint: "Řádky na tiketu s pevnou cenou zůstanou zamčené — server u nich vrací 409.",
      addTags: "Přidat štítky",
      addTagsPlaceholder: "oddělené čárkou, např. backend, urgentní",
      removeTags: "Odebrat štítky",
      removeTagsPlaceholder: "oddělené čárkou",
      save_one: "Uložit změny u {{count}} záznamu",
      save_few: "Uložit změny u {{count}} záznamů",
      save_other: "Uložit změny u {{count}} záznamů",
      pickField: "Nejdřív vyberte pole k úpravě",
      confirmReassign_one: "Přiřadit {{count}} záznam vybranému uživateli?",
      confirmReassign_few: "Přiřadit {{count}} záznamy vybranému uživateli?",
      confirmReassign_other: "Přiřadit {{count}} záznamů vybranému uživateli?",
      confirmAddTags_one: "Přidat štítky [{{tags}}] k {{count}} záznamu?",
      confirmAddTags_few: "Přidat štítky [{{tags}}] ke {{count}} záznamům?",
      confirmAddTags_other: "Přidat štítky [{{tags}}] k {{count}} záznamům?",
      updated_one: "Upraven {{count}} záznam",
      updated_few: "Upraveny {{count}} záznamy",
      updated_other: "Upraveno {{count}} záznamů",
      failed: "Hromadná úprava se nezdařila",
    },
  },
};
